import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { type DatabaseHistoryEntry, useDatabaseHistories } from '@/hooks/useDatabaseHistories'
import { type WalletHistory, fetchUserOnchainHistories } from '@/services/api'
import type { OrderStatus } from '@/types/history'
import { formatPrimaryAmount, formatSectionLabel, formatTimeLabel } from '@/utils/format'
import { type GroupedWalletHistory, groupHistoryByTxHash } from '@/utils/historyGrouping'
import { MainLayout } from '@components/layouts/MainLayout'
import {
  HistoryItem,
  type HistoryItemProps,
  type HistoryItemType,
} from '@components/ui/HistoryItem'
import { IconButton } from '@components/ui/IconButton'
import { Tabs } from '@components/ui/Tabs'

interface HistoryEntry extends Omit<HistoryItemProps, 'type'> {
  id: string
  type: HistoryItemType
  createdAt: Date
  amount: number
  orderStatus?: OrderStatus | null
}

interface HistorySection {
  label: string
  entries: HistoryEntry[]
  dateKey: string
}

interface UnifiedHistoryEntry {
  id: string
  type: 'db'
  createdAt: Date
  historyEntry: HistoryEntry
}

const TABS = [
  { id: 'all', label: 'すべて' },
  { id: 'received', label: '受け取った' },
  { id: 'sent', label: '送った' },
] as const

type TabId = (typeof TABS)[number]['id']

const resolveHistoryType = (amount: number): HistoryItemType => (amount >= 0 ? 'receive' : 'send')

/** 0x で始まるアドレスを `0x1234...abcd` 形式に短縮 */
const shortenAddress = (text: string): string =>
  text.replace(/0x[0-9a-fA-F]{40}/g, (addr) => `${addr.slice(0, 6)}...${addr.slice(-4)}`)

const toHistoryEntry = (history: GroupedWalletHistory): HistoryEntry => {
  const createdAt = new Date(history.createdAt)
  const feeEntry = history.feeEntry
  const description = history.description ? shortenAddress(history.description) : undefined
  const feeText = feeEntry
    ? `手数料: ${formatPrimaryAmount(feeEntry.amount, feeEntry.asset?.symbol ?? '')}`
    : undefined
  const secondaryAmount = [description, feeText].filter(Boolean).join('\n') || undefined
  return {
    id: history.id,
    type: resolveHistoryType(history.amount),
    title: history.category?.name ?? '取引',
    primaryAmount: formatPrimaryAmount(history.amount, history.asset?.symbol ?? ''),
    secondaryAmount,
    timeLabel: formatTimeLabel(createdAt),
    createdAt,
    amount: history.amount,
    orderStatus: history.order?.status ?? null,
  }
}

const createUnifiedHistoryEntry = (entry: GroupedWalletHistory): UnifiedHistoryEntry => {
  return {
    id: entry.id,
    type: 'db',
    createdAt: new Date(entry.createdAt),
    historyEntry: toHistoryEntry(entry),
  }
}

export const History: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('all')
  const [isSyncing, setIsSyncing] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  // DB履歴取得（オンチェーン履歴を含む）
  const dbHistoriesQuery = useDatabaseHistories()

  // 無限スクロールの実装
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (
          entry.isIntersecting &&
          dbHistoriesQuery.hasNextPage &&
          !dbHistoriesQuery.isFetchingNextPage
        ) {
          dbHistoriesQuery.fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current)
      }
    }
  }, [
    dbHistoriesQuery.hasNextPage,
    dbHistoriesQuery.isFetchingNextPage,
    dbHistoriesQuery.fetchNextPage,
  ])

  // DB履歴の処理（WalletHistory[]形式 → txHashでグルーピング）
  const historyEntries = useMemo(() => {
    if (!dbHistoriesQuery.data?.pages) return []
    const dbEvents = dbHistoriesQuery.data.pages.flatMap((page) => page.events)
    const walletHistories = dbEvents.map(
      (event: DatabaseHistoryEntry): WalletHistory => ({
        id: event.id,
        amount: event.amount,
        category: {
          id: event.type,
          name: event.title,
        },
        description: event.secondaryAmount,
        createdAt:
          event.createdAt instanceof Date
            ? event.createdAt.toISOString()
            : new Date(event.createdAt).toISOString(),
        asset: event.asset,
        onchainHistory: event.onchainHistory,
        order: event.order,
      })
    )
    return groupHistoryByTxHash(walletHistories)
  }, [dbHistoriesQuery.data])

  // 履歴をフィルタリング
  const filteredEntries = useMemo(() => {
    switch (activeTab) {
      case 'received':
        return historyEntries.filter((entry) => entry.amount >= 0)
      case 'sent':
        return historyEntries.filter((entry) => entry.amount < 0)
      default:
        return historyEntries
    }
  }, [activeTab, historyEntries])

  // クエリの状態管理
  const isLoading = dbHistoriesQuery.isLoading
  const isFetching = dbHistoriesQuery.isFetching
  const hasError = !!dbHistoriesQuery.error
  const error = dbHistoriesQuery.error

  const sections = useMemo(() => {
    const sectionMap = new Map<string, HistorySection>()

    // 履歴エントリをセクションに追加
    for (const entry of filteredEntries) {
      const unifiedEntry = createUnifiedHistoryEntry(entry)
      const key = unifiedEntry.createdAt.toISOString().slice(0, 10)
      const label = formatSectionLabel(unifiedEntry.createdAt)
      const existing = sectionMap.get(key)
      if (existing) {
        existing.entries.push(unifiedEntry.historyEntry)
      } else {
        sectionMap.set(key, {
          label,
          entries: [unifiedEntry.historyEntry],
          dateKey: key,
        })
      }
    }

    // 各セクション内のエントリを時系列順にソート
    for (const section of sectionMap.values()) {
      section.entries.sort((a, b) => {
        const aTime =
          a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime()
        const bTime =
          b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime()
        return bTime - aTime
      })
    }

    // 時系列順にソート（日付降順）
    return Array.from(sectionMap.values()).sort((a, b) => b.dateKey.localeCompare(a.dateKey))
  }, [filteredEntries])

  return (
    <MainLayout
      activeMain="history"
      header={{
        enabled: true,
        title: '履歴',
        leftIcon: null,
        rightIcon: null,
        leftSlot: (
          <IconButton aria-label="前へ" icon="Back" className="invisible pointer-events-none" />
        ),
        rightSlot: (
          <button
            type="button"
            aria-label="再検索"
            className="inline-flex items-center justify-center size-8 rounded-full text-gray-300 hover:text-gray-400 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={async () => {
              if (isSyncing) return
              setIsSyncing(true)
              try {
                // オンチェーン履歴を同期
                await fetchUserOnchainHistories()
                // 履歴キャッシュを無効化して再取得
                await queryClient.invalidateQueries({ queryKey: ['histories', 'database'] })
              } catch (error) {
                console.error('Failed to fetch onchain histories:', error)
              } finally {
                setIsSyncing(false)
              }
            }}
            disabled={isSyncing}
          >
            <svg
              className={`size-5 ${isSyncing ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <title>再検索</title>
              <path
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ),
        className: 'flex h-16 w-full items-center justify-between px-6',
        containerClassName: 'px-4 pt-4',
        scrolledClassName: 'supports-[backdrop-filter]:backdrop-blur-[1px]',
      }}
      outerClassName="bg-white"
      className="bg-white"
      contentClassName="px-4 pb-10"
    >
      <div className="flex flex-col gap-6">
        <Tabs options={TABS} value={activeTab} onChange={(id) => setActiveTab(id as TabId)} />
        <div className="flex flex-col gap-8">
          {/* 検索の進行状況表示 */}
          {(isLoading || isFetching) && (
            <div className="py-4 text-center text-sm font-medium text-blue-600 bg-blue-50 rounded-lg">
              取引履歴を読み込み中...
            </div>
          )}

          {/* エラーハンドリング */}
          {hasError && (
            <div className="py-4 text-center text-sm font-medium text-red-600 bg-red-50 rounded-lg">
              {error instanceof Error
                ? error.message
                : '履歴を読み込めませんでした。再度お試しください。'}
            </div>
          )}

          {/* 通常の取引履歴表示 */}
          {!hasError && sections.length === 0 && !isLoading ? (
            <div className="py-10 text-center text-sm font-medium text-[#71717a]">
              取引履歴はまだありません。
            </div>
          ) : (
            <AnimatePresence initial={false} mode="sync">
              {sections.map((section) => (
                <motion.section
                  key={section.dateKey}
                  className="flex flex-col gap-4"
                  layout
                  initial={{ opacity: 0, x: 48 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -48 }}
                  transition={{ duration: 0.24, ease: 'easeOut' }}
                >
                  <header className="flex items-center justify-between">
                    <h2 className="text-sm font-medium text-[#71717a]">{section.label}</h2>
                  </header>
                  <div className="flex flex-col gap-2">
                    <AnimatePresence initial={false} mode="sync">
                      {section.entries.map((entry, index) => (
                        <motion.div
                          key={`${entry.id}-${entry.type}-${index}`}
                          layout
                          initial={{ opacity: 0, x: 32 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -32 }}
                          transition={{ duration: 0.2, ease: 'easeOut' }}
                        >
                          <HistoryItem {...entry} />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </motion.section>
              ))}
            </AnimatePresence>
          )}

          {/* 無限スクロールのトリガー要素 */}
          <div ref={loadMoreRef} className="py-4" />

          {/* 次のページ読み込み中インジケーター */}
          {dbHistoriesQuery.isFetchingNextPage && (
            <div className="py-4 text-center text-sm font-medium text-blue-600 bg-blue-50 rounded-lg">
              履歴を読み込み中...
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  )
}

export default History
